import type { SxProps, Theme, TypographyProps } from "@mui/material";
import { Typography, styled } from "@mui/material";
import { FormattedMessage } from "react-intl";

import { InfoTooltip } from "@citrine/atoms/info-tooltip";
import { WarningIcon } from "@citrine/atoms/warning-icon";
import type { TranslationID } from "@citrine/lang/utils";
import { merge } from "@citrine/theme/style";

import * as sx from "./index.sx";

type HeaderProps = {
  "data-testid"?: string;
  className?: string;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  variant?: TypographyProps["variant"];
  tooltipTranslationId?: TranslationID;
  tooltipHeaderTranslationId?: TranslationID;
  warningIconTooltip?: React.ReactElement | string;
  warningIconTooltipTestId?: string;
} & (
  | {
      translationId: TranslationID;
      title?: never;
    }
  | {
      title: React.ReactNode;
      translationId?: never;
    }
);

export function Header({
  children,
  title,
  translationId,
  tooltipTranslationId,
  tooltipHeaderTranslationId,
  variant = "h2",
  warningIconTooltip,
  warningIconTooltipTestId,
  ...passThroughProps
}: HeaderProps) {
  return (
    <sx.Header {...passThroughProps}>
      <sx.TooltipWrapper>
        <Typography variant={variant}>
          {title ?? <FormattedMessage id={translationId} />}
        </Typography>
        {tooltipTranslationId ? (
          <InfoTooltip
            id={tooltipTranslationId}
            headerId={tooltipHeaderTranslationId}
          />
        ) : null}
        {warningIconTooltip ? (
          <WarningIcon
            data-testid={warningIconTooltipTestId}
            title={warningIconTooltip}
          />
        ) : null}
      </sx.TooltipWrapper>
      <div>{children}</div>
    </sx.Header>
  );
}

export const SectionHeader = styled(Header)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const WorkflowHeader = (props: Exclude<HeaderProps, "variant">) => (
  <Header
    {...props}
    sx={merge(props.sx ?? null, (theme) => ({
      margin: theme.spacing(0, 0, 3, 0),
      "& strong": {
        fontWeight: theme.typography.fontWeightBold,
      },
    }))}
    variant="h3"
  />
);

/**
 * Intended for bolding numeric values within the WorkflowHeader
 */
export function WorkflowHeaderNumber({
  className,
  value,
}: {
  value: number | string;
  className?: string;
}) {
  return (
    <Typography className={className} component="span" variant="h3">
      <strong>{value}</strong>
    </Typography>
  );
}
